// =============================================================================
// Plan Data Types
// =============================================================================

/**
 * Raw plan data structure from API /properties/{propertyId}/plans/search
 * Data returned in camelCase format
 */
export interface PropertyPlansApiResponse {
  id: string;
  property: {
    id: string;
    nameAbbreviation: string;
    departmentCode: number;
    createdAt?: string;
    updatedAt?: string;
    createdUser?: string;
    updatedUser?: string;
    createdStaff?: string | null; // Formatted staff name from formatStaff (string or null)
    updatedStaff?: string | null; // Formatted staff name from formatStaff (string or null)
    salestaffName?: string | null;
    constructionCompanyName?: string | null;
    /** Nested construction company from API; PropertySection prefers `nameAbbreviation` (fallback to `constructionCompanyName`, then `name`) */
    constructionCompany?: {
      id?: string;
      name?: string | null;
      nameAbbreviation?: string | null;
    } | null;
    /** Same as customerSupplier.abbreviationName; set by plan detail API for convenience */
    customerSupplierName?: string | null;
    customerSupplier?: {
      id?: string;
      formalName?: string;
      abbreviationName?: string | null;
    } | null;
  };
  planDeliveries: CreatePlanRequest[];
  canUpdate: boolean;
}

/**
 * Base interface containing common fields shared between Plan and CreatePlanRequest.
 * Uses strict (required) types for fields that are always present in API response (Plan).
 * CreatePlanRequest overrides placementDate, orderStatus, formulationProductType, vehicleSize
 * to allow null/empty for form defaults.
 */
export interface BasePlan {
  placementDate: string;
  timePossible: string;
  timePossibleRemarks?: string | null;
  placeToPlace: string;
  formulationProductType: number;
  formulationProduct: string;
  vehicleSize: number;
  pitchHope?: string | null;
  pitchPossible?: string | null;
  placingMethodOthers?: string | null;
  witnessName: string;
  witnessTel: string;
  orderStatus: number;
  remarks?: string | null;
}

/**
 * Plan interface for reading/displaying plan data
 * Extends BasePlan and adds read-only fields and computed properties
 */
export interface Plan extends BasePlan {
  // Read-only fields from API
  planId: string;
  personInChargeName: string;
  abbreviationName: string;
  constructionCompanyNameAbbreviation: string;
  propertyNameAbbreviation: string;
  departmentCode: number;
  propertyId: string;
  quantity: number;
  quantitySuffix?: string | null;
  precedingMaterialType?: string | null;
  precedingMaterialQuantity?: number | null;
  placingMethod?: string | null;
  witnessCompanyName?: string | null;
  isPreconfirmed: boolean;
  isArchive: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;

  // Override base fields with specific types for Plan
  remarks: string; // Required in Plan, optional in base

  // Computed properties for backward compatibility
  id: string;
  planName: string;
  constructionCompanyName: string;
  propertyName: string;
  constructionSiteNumber: string;
  nextDay: string;
  isArchived: boolean;
  scheduledDate: string;
  operatingTime: string;
  canUpdate: boolean;
}

// =============================================================================
// API Request/Response Types
// =============================================================================
export interface GetPlansParams {
  planName?: string;
  constructionCompanyName?: string;
  propertyName?: string;
  constructionSiteNumber?: string;
  quantity?: number;
  placementDateFrom?: string;
  placementDateTo?: string;
  nextDayFrom?: string;
  nextDayTo?: string;
  hasArchived?: boolean;
  sort?: PlanSortField;
  order?: SortOrder;
  page?: number;
  perPage?: number;
}

export interface GetPlansResponse {
  items: Plan[];
  pagination: {
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
  };
}

export type PlanApiItem = Plan;

// =============================================================================
// Create Plan Types
// =============================================================================

/**
 * Create Plan Request interface for creating new plans.
 * Extends BasePlan and overrides fields that may be null/empty in the form (default values).
 */
export interface CreatePlanRequest
  extends Omit<
    BasePlan,
    'placementDate' | 'orderStatus' | 'formulationProductType' | 'vehicleSize'
  > {
  // Override base: allow null/empty for form defaults
  placementDate?: string | null;
  orderStatus?: number | null;
  formulationProductType?: number | null;
  vehicleSize?: number | null;

  // Fields specific to create request
  customerSupplierId: string | null;
  timeHope?: string | null;
  quantityUnit?: string | null;
  witnessCompanyId?: string | null;
  remarksForClient?: string | null;
  id?: string | null;

  // Fields with different types than Plan
  quantity: number | null;
  quantitySuffix?: number | null;
  precedingMaterialType?: number | null;
  precedingMaterialQuantity?: string | number | null;
  precedingMaterialUnit?: string | null;
  placingMethod?: number | null;
  placingMethodOthers?: string | null;
  isArchive?: boolean | string;
  witnessCompany?: {
    id: string;
    name: string;
  };
  customerSupplier?: {
    id: string;
    abbreviationName: string;
  };
  isDeleted?: boolean;
}

export interface CreateFullPlanRequest {
  plans: CreatePlanRequest[];
  propertyId: string;
}

/**
 * Update Plan Request interface for updating existing plans
 * Extends CreatePlanRequest with all fields optional (using Partial)
 * Adds displayOrder for update operations
 * For creates: id is omitted
 * For updates: id is required
 * For deletes: id is required and isDeleted is true
 */
export interface UpdatePlanRequest extends Partial<CreatePlanRequest> {
  /**
   * Plan delivery ID - required for updates and deletes, omitted for creates
   */
  id?: string;
  /**
   * Display order - used for update operations to maintain order
   */
  displayOrder?: number;
}

export interface UpdateFullPlanRequest {
  plans: UpdatePlanRequest[];
  propertyId: string;
}

// =============================================================================
// Archive/Confirm Types
// =============================================================================
export interface PlanIdentifier {
  id: string;
}

export interface ArchivePlansRequest {
  planLists: PlanIdentifier[];
  isArchive: boolean;
}

export interface ArchivePlansResponse {
  success: PlanIdentifier[];
  notFound: PlanIdentifier[];
}

export interface ConfirmPlansRequest {
  planLists: PlanIdentifier[];
  isConfirm: boolean;
}

export interface ConfirmPlansResponse {
  success: PlanIdentifier[];
  notFound: PlanIdentifier[];
}

// =============================================================================
// Table Types
// =============================================================================
export interface PlanTableProps {
  onPlanClick?: (plan: Plan) => void;
  data?: Plan[];
  loading?: boolean;
  currentPage?: number;
  itemsPerPage?: number;
  totalItems?: number;
  selectedPlans?: string[];
  onPageChange?: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  onSelectionChange?: (selectedPlans: string[]) => void;
  onArchive?: (planIds: string[]) => Promise<void>;
  onRestore?: (planIds: string[]) => Promise<void>;
  onConfirm?: (planIds: string[], isConfirm: boolean) => Promise<void>;
}

// =============================================================================
// State Management Types
// =============================================================================
export interface BulkActionState {
  selectedPlans: string[];
  isArchiving: boolean;
  isRestoring: boolean;
}

export interface PaginationState {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
}

export type SortOrder = 'asc' | 'desc';

export type SortableField =
  | 'planName'
  | 'constructionCompanyName'
  | 'propertyName'
  | 'placementDate'
  | 'nextDay'
  | 'updatedAt';

export interface SortState {
  field: SortableField | null;
  order: SortOrder;
}

// =============================================================================
// Enums
// =============================================================================
export enum PlanSortField {
  PLAN_NAME = 'planName',
  CONSTRUCTION_COMPANY_NAME = 'constructionCompanyName',
  PROPERTY_NAME = 'propertyName',
  PLACEMENT_DATE = 'placementDate',
  NEXT_DAY = 'nextDay',
  UPDATED_AT = 'updatedAt',
}
